import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import LanguageSelector from '../../components/common/LanguageSelector';
import CitySearch from '../../components/feature/CitySearch';
import { cityNameToUrlSlug, getAllCountries, countryNameToUrlSlug } from '../../utils/cityData';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

interface TimeZone {
  name: string;
  timezone: string;
  flag: string;
}

export default function WorldClock() {
  const { t, i18n } = useTranslation(['worldclock', 'common']);
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userTimezone, setUserTimezone] = useState('UTC');
  const [isFullscreen, setIsFullscreen] = useState(false);


  // 使用useMemo确保翻译函数准备好后再创建timeZones数组
  const timeZones: TimeZone[] = useMemo(() => [
    { name: t('city.beijing'), timezone: 'Asia/Shanghai', flag: '🇨🇳' },
    { name: t('city.tokyo'), timezone: 'Asia/Tokyo', flag: '🇯🇵' },
    { name: t('city.shanghai'), timezone: 'Asia/Shanghai', flag: '🇨🇳' },
    { name: t('city.singapore'), timezone: 'Asia/Singapore', flag: '🇸🇬' },
    { name: t('city.sydney'), timezone: 'Australia/Sydney', flag: '🇦🇺' },
    { name: t('city.london'), timezone: 'Europe/London', flag: '🇬🇧' },
    { name: t('city.paris'), timezone: 'Europe/Paris', flag: '🇫🇷' },
    { name: t('city.berlin'), timezone: 'Europe/Berlin', flag: '🇩🇪' },
    { name: t('city.moscow'), timezone: 'Europe/Moscow', flag: '🇷🇺' },
    { name: t('city.newYork'), timezone: 'America/New_York', flag: '🇺🇸' },
    { name: t('city.losAngeles'), timezone: 'America/Los_Angeles', flag: '🇺🇸' },
    { name: t('city.chicago'), timezone: 'America/Chicago', flag: '🇺🇸' }
  ], [t]);

  useEffect(() => {
    // 更新SEO配置
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('worldclock', currentLang);
    updateSEOTags(seoConfig, currentLang);
  }, [i18n.language]);

  // 添加定时器自动更新时间
  useEffect(() => {
    // 获取用户时区
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimezone(userTz);

    // 设置定时器每秒更新时间
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // 组件卸载时清除定时器
    return () => clearInterval(timer);
  }, []);

  // 随机排序用户评价
  const [randomizedReviews, setRandomizedReviews] = useState<Array<{
    id: number;
    avatar: string;
    name: string;
    rating: number;
    review: string;
  }>>([]);

  useEffect(() => {
    // 创建用户评价数据，使用当前语言的翻译
    const userReviews = [
      {
        id: 1,
        avatar: "/images/avatars/Global Business.jpg",
        name: t('user.globalBusiness'),
        rating: 5,
        review: t('reviews.globalBusiness')
      },
      {
        id: 2,
        avatar: "/images/avatars/Remote Developer.jpg",
        name: t('user.remoteTeam'),
        rating: 5,
        review: t('reviews.remoteTeam')
      },
      {
        id: 3,
        avatar: "/images/avatars/Travel Blogger.jpg",
        name: t('user.travelBlogger'),
        rating: 4,
        review: t('reviews.travelBlogger')
      },
      {
        id: 4,
        avatar: "/images/avatars/International Family.jpg",
        name: t('user.internationalFamily'),
        rating: 5,
        review: t('reviews.internationalFamily')
      },
      {
        id: 5,
        avatar: "/images/avatars/Manager Zhang.jpg",
        name: t('user.managerZhang'),
        rating: 5,
        review: t('reviews.managerZhang')
      }
    ];

    // 只在语言改变时重新生成和随机排序，但保持同一语言下的稳定性
    const shuffled = [...userReviews].sort(() => Math.random() - 0.5);
    setRandomizedReviews(shuffled);
  }, [t, i18n.language]);


  const formatTime = (timezone: string) => {
    // Add timezone validation
    if (!timezone) {
      timezone = 'UTC';
    }

    try {
      return currentTime.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.warn(`Invalid timezone format: ${timezone}`);
      return currentTime.toLocaleTimeString('en-US', {
        timeZone: 'UTC',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  };

  const formatDate = useCallback((timezone: string) => {
    // Add timezone validation
    if (!timezone) {
      timezone = 'UTC';
    }

    // 根据当前语言设置语言环境
    const locale = i18n.language === 'zh' ? 'zh-CN' : 'en-US';

    try {
      return currentTime.toLocaleDateString(locale, {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long'
      });
    } catch (error) {
      console.warn(`Invalid timezone format: ${timezone}`);
      return currentTime.toLocaleDateString(locale, {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long'
      });
    }
  }, [currentTime, i18n.language]);

  const getTimeOffset = (timezone: string) => {
    if (!timezone) {
      timezone = 'UTC';
    }

    try {
      const offset = getTimezoneOffset(timezone);
      return offset >= 0 ? `UTC+${offset}` : `UTC${offset}`;
    } catch (error) {
      return 'UTC+0';
    }
  };

  const getTimezoneOffset = (timezone: string) => {
    try {
      const now = new Date();
      const utcTime = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
      const targetTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
      return (targetTime.getTime() - utcTime.getTime()) / (1000 * 60 * 60);
    } catch (error) {
      return 0; // Default to UTC offset
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleFullscreenKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsFullscreen(false);
    }
  };

  // 城市名称映射 - 将翻译后的名称转换为标准英文名称
  const getStandardCityName = (translatedName: string): string => {
    const cityMap: Record<string, string> = {
      [t('city.beijing')]: 'Beijing',
      [t('city.tokyo')]: 'Tokyo', 
      [t('city.shanghai')]: 'Shanghai',
      [t('city.singapore')]: 'Singapore',
      [t('city.sydney')]: 'Sydney',
      [t('city.london')]: 'London',
      [t('city.paris')]: 'Paris',
      [t('city.berlin')]: 'Berlin',
      [t('city.moscow')]: 'Moscow',
      [t('city.newYork')]: 'New York',
      [t('city.losAngeles')]: 'Los Angeles',
      [t('city.chicago')]: 'Chicago'
    };
    return cityMap[translatedName] || translatedName;
  };

  const handleCityClick = (cityName: string) => {
    // 将翻译后的城市名转换为标准英文名称
    const standardCityName = getStandardCityName(cityName);
    console.log('Translated city name:', cityName, 'Standard name:', standardCityName);

    // 将城市名转换为URL友好的格式
    const citySlug = cityNameToUrlSlug(standardCityName);
    console.log('City slug:', citySlug);
    navigate(`/worldclock/time-in-${citySlug}`);
  };

  const handleSearchCitySelect = (cityName: string) => {
    // 将城市名转换为URL友好的格式并跳转
    const citySlug = cityNameToUrlSlug(cityName);
    console.log('Searching for city:', cityName, 'URL slug:', citySlug);
    const targetUrl = `/worldclock/time-in-${citySlug}`;
    console.log('Navigating to:', targetUrl);
    navigate(targetUrl);
  };

  // Fullscreen modal
  if (isFullscreen) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
        onClick={toggleFullscreen}
        onKeyDown={handleFullscreenKeyPress}
        tabIndex={0}
      >
        <div className="text-center" onClick={(e) => e.stopPropagation()}>
          {/* Exit button */}
          <button
            onClick={toggleFullscreen}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          
          {/* Large time display */}
          <div className="mb-12">
            <div className="text-[3.75rem] md:text-[8rem] font-mono font-[700] text-blue-400 mb-8">
              {formatTime(userTimezone)}
            </div>
            
            {/* Date and timezone info */}
            <div className="space-y-4 text-white">
              <div className="text-2xl">
                {formatDate(userTimezone)}
              </div>
              <div className="text-lg text-gray-300">
                {t('timezone.local')}: {userTimezone} ({getTimeOffset(userTimezone)})
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Time Display */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <div className="text-center">
            <div className="flex justify-between items-start mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">{t('timezone.currentTime')}</h2>
              
              {/* Fullscreen button - moved to top right */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleFullscreen}
                  className="text-gray-600 hover:text-blue-600 transition-colors p-2 hover:bg-gray-100 rounded-lg flex items-center space-x-2"
                  title="Fullscreen"
                >
                  <span className="hidden md:block text-sm font-medium">{t('common:label.fullscreen')}</span>
                  <i className="ri-picture-in-picture-2-line text-xl"></i>
                </button>
              </div>
            </div>
            
            {/* Time display container - unified height structure */}
            <div className="mb-8 md:mb-10">
              <div className="flex items-center justify-center mb-6 md:mb-8">
                <div className="text-[3.75rem] md:text-8xl font-mono font-bold text-blue-600">
                  {formatTime(userTimezone)}
                </div>
              </div>
              
              {/* Additional time info */}
              <div className="space-y-2 md:space-y-3">
                <div className="text-lg md:text-xl text-gray-600">
                  {formatDate(userTimezone)}
                </div>
                <div className="text-sm md:text-base text-gray-500">
                  {t('timezone.local')}: {userTimezone} ({getTimeOffset(userTimezone)})
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* World Clock Grid */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-800">{t('page.title')}</h3>
            <div className="hidden md:block w-72">
              <CitySearch onCitySelect={handleSearchCitySelect} />
            </div>
          </div>
          
          {/* Mobile search */}
          <div className="md:hidden mb-6">
            <CitySearch onCitySelect={handleSearchCitySelect} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {timeZones.map((tz) => {
              const isBeijing = tz.name === t('city.beijing');

              // 北京城市卡片使用a标签
              if (isBeijing) {
                return (
                  <a
                    key={tz.timezone}
                    href="/beijing-time"
                    className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors cursor-pointer block"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">{tz.flag}</span>
                        <span className="font-semibold text-gray-800 text-lg">{tz.name}</span>
                      </div>
                      <span className="text-xs text-gray-500">{getTimeOffset(tz.timezone)}</span>
                    </div>
                    <div className="text-3xl font-mono font-bold text-gray-900 mb-1">
                      {formatTime(tz.timezone)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatDate(tz.timezone)}
                    </div>
                  </a>
                );
              }

              // 其他城市卡片采用简洁设计，点击整个卡片跳转
              return (
                <div
                  key={tz.timezone}
                  className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => handleCityClick(tz.name)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{tz.flag}</span>
                      <span className="font-semibold text-gray-800 text-lg">{tz.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">{getTimeOffset(tz.timezone)}</span>
                  </div>
                  <div className="text-3xl font-mono font-bold text-gray-900 mb-1">
                    {formatTime(tz.timezone)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatDate(tz.timezone)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Countries Time Links */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h3 className="text-xl font-bold text-gray-800 mb-6">
            {t('countriesTime', { defaultValue: 'Time by Country' })}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {getAllCountries().map((country) => {
              const displayName = i18n.language.startsWith('zh') ? country.countryNameZh : country.countryName;
              const countrySlug = countryNameToUrlSlug(i18n.language.startsWith('zh') ? country.countryNameZh : country.countryName);
              return (
                <a
                  key={country.countryCode}
                  href={`/worldclock/country/${countrySlug}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline text-sm transition-colors"
                >
                  {t('timeIn', { defaultValue: 'Time in' })} {displayName}
                </a>
              );
            })}
          </div>
        </div>

        {/* Usage Instructions */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('howto.title')}</h3>
          <div className="space-y-3 md:space-y-4 text-gray-600">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">1</div>
              <p className="text-sm md:text-base">{t('howto.step1')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">2</div>
              <p className="text-sm md:text-base">{t('howto.step2')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">3</div>
              <p className="text-sm md:text-base">{t('howto.step3')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">4</div>
              <p className="text-sm md:text-base">{t('howto.step4')}</p>
            </div>
          </div>
        </div>

        {/* User Reviews Module */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('reviews.title')}</h3>
          <div className="relative overflow-x-auto">
            <div className="flex overflow-x-auto space-x-4 md:space-x-8 scrollbar-hide">
              {randomizedReviews.map((review) => (
                <div key={review.id} className="flex-shrink-0 bg-gray-50 p-4 md:p-6 rounded-lg w-80">
                  <div className="flex items-center mb-3 md:mb-4">
                    <img 
                      src={review.avatar}
                      alt={`${review.name} Avatar`} 
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex text-yellow-400 mb-1">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`${i < review.rating ? 'ri-star-fill' : 'ri-star-line'} text-sm`}></i>
                        ))}
                      </div>
                      <span className="text-xs md:text-sm text-gray-600 font-medium">{review.name}</span>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">{review.review}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-100 text-gray-800 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
            {/* Left side - Website description */}
            <div className="lg:w-2/3">
              <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('common:footer.about')}</h4>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {t('common:footer.aboutDesc')}
              </p>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <i className="ri-timer-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.timer')}</span> - {t('common:footer.timerDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-play-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.stopwatch')}</span> - {t('common:footer.stopwatchDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-global-line text-purple-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.worldclock')}</span> - {t('common:footer.worldClockDesc')}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <a href="/terms" className="hover:text-gray-700 transition-colors">{t('common:footer.termsOfService')}</a>
                  <span>|</span>
                  <a href="#" className="hover:text-gray-700 transition-colors">{t('common:footer.privacy')}</a>
                  <span>|</span>
                  <a href="mailto:mu9347mu@gmail.com?subject=Contact Us - timer.today&body=Hello timer.today team,%0D%0A%0D%0AI would like to..." className="hover:text-gray-700 transition-colors">{t('common:footer.contactUs')}</a>
                  <span>|</span>
                  <span>{t('common:footer.copyright')}</span>
                  <span className="lg:hidden">|</span>
                  <div className="lg:hidden">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </div>

              {/* Vertical divider - only visible on large screens */}
              <div className="hidden lg:flex w-px bg-gray-300 mx-6 h-40 self-center"></div>

            {/* Right side - Multilingual content */}
            <div className="hidden lg:block lg:w-1/3">
              <div className="space-y-3 text-sm text-gray-600 mt-12">
                <p>world clock time zones international time </p>
                <p>世界時計 タイムゾーン 国際時間 今何時</p>
                <p>reloj mundial zonas horarias tiempo internacional qué hora</p>
                <p>Weltzeituhr Zeitzonen internationale Zeit wie spät</p>
                <p>мировые часы часовые пояса международное время который час</p>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
